import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Shield,
  Edit3,
  Save,
  X,
  Camera,
  Settings,
  Activity,
  Users,
  Briefcase,
  Sparkles,
  PhoneCall,
  MessageSquare,
  RefreshCcw
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import ThemeToggle from '../../components/ThemeToggle';
import LocationMapPicker from '../../components/LocationMapPicker';

const getRoleDisplay = (role) => {
  if (!role) return 'User';
  switch (role.toLowerCase()) {
    case 'super_admin':
    case 'superadmin':
      return 'Super Admin';
    case 'admin':
      return 'Admin';
    case 'customer':
    case 'user':
      return 'Customer';
    default:
      return role.charAt(0).toUpperCase() + role.slice(1);
  }
};

const API_BASE_URL = 'https://api.trackyourwheelz.com';

const presetAvatars = [
  'https://plus.unsplash.com/premium_photo-1739201499644-7878cbb4cdf9?q=80&w=1035&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://plus.unsplash.com/premium_photo-1738449258706-74c1dc94b988?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfDE4fHx8ZW58MHx8fHx8',
  'https://plus.unsplash.com/premium_photo-1739088004528-bdaafb2f5f25?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfDQxfHx8ZW58MHx8fHx8',
  'https://media.istockphoto.com/id/1474386163/photo/young-professional-man-business-portrait-head-shirt-3d-rendering-character.webp?a=1&s=612x612&w=0&k=20&c=vezZTNAk1rFUVRj_5dbESI9h5jqegKN-A3FKr600h-4='
];

const getRandomAvatar = () => {
  const index = Math.floor(Math.random() * presetAvatars.length);
  return presetAvatars[index];
};

const resolveAvatarUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const normalizedBase = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  const resolved = `${normalizedBase}${normalizedPath}`;
  return resolved;
};

const mapProfileResponse = (payload, randomAvatar) => {
  // Handle accounts API response structure
  const address = payload.address || {};
  const fullAddress = [
    address.house_no,
    address.street,
    address.sub_area,
    address.area,
    address.landmark,
    address.pincode ? `Pincode: ${address.pincode}` : ''
  ].filter(Boolean).join(', ');

  // Get avatar URL from various possible fields (prioritize avatarUrl)
  const avatarUrlFromPayload = payload.avatarUrl || payload.avatar_url || payload.avatar || null;
  const resolvedAvatarUrl = resolveAvatarUrl(avatarUrlFromPayload);
  
  // Use resolved avatar URL if available, otherwise use random preset avatar
  const finalAvatar = resolvedAvatarUrl || randomAvatar;

  return {
    id: payload.id,
    name: payload.name || 'N/A',
    email: payload.email || 'N/A',
    phone: payload.phone || '',
    role: getRoleDisplay(payload.role),
    rawRole: payload.role || '',
    location: fullAddress || payload.location || '',
    joinDate: payload.created_at || payload.createdAt || '',
    lastLogin: payload.updated_at || payload.updatedAt || payload.createdAt || '',
    status: payload.status === 'active' || payload.isActive ? 'Active' : 'Inactive',
    avatar: finalAvatar,
    avatarUrl: resolvedAvatarUrl,
    rating: payload.rating ?? null,
    company: payload.company || '',
    secondaryCompany: payload.secondaryCompany || '',
    // Account specific fields
    gstNumber: payload.gst_number || null,
    panNumber: payload.pan_number || null,
    tanNumber: payload.tan_number || null,
    addressId: payload.address_id || null,
    createdBy: payload.created_by || null,
    // Address details
    address: {
      id: address.id || null,
      houseNo: address.house_no || '',
      street: address.street || '',
      subArea: address.sub_area || '',
      area: address.area || '',
      landmark: address.landmark || '',
      cityId: address.city_id || null,
      stateId: address.state_id || null,
      country: address.country || null,
      pincode: address.pincode || null,
      geocoordinates: address.geocoordinates || null,
      latitude: address.latitude || null,
      longitude: address.longitude || null,
      fullAddress: fullAddress
    }
  };
};

const getInitials = (name = '') => {
  if (!name) return 'NA';
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0].toUpperCase())
    .slice(0, 2)
    .join('') || 'NA';
};

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { get, put } = useApi();
  const { user, hasRole, hasAnyRole } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [profileData, setProfileData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [randomAvatar] = useState(() => getRandomAvatar());
  const [locationCoords, setLocationCoords] = useState({
    latitude: '',
    longitude: '',
    geocoordinates: '',
  });
  const [isSavingLocation, setIsSavingLocation] = useState(false);

  // Check if user is admin
  const isAdmin = hasAnyRole(['admin', 'super_admin', 'superadmin']);
  const settingsPath = location.pathname.startsWith('/admin')
    ? '/admin/profile-settings'
    : '/customer/profile-settings';

  const formatDate = (dateString, includeTime = false) => {
    if (!dateString) return 'N/A';
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) {
      return dateString;
    }
    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      ...(includeTime && { hour: '2-digit', minute: '2-digit' })
    });
  };

  useEffect(() => {
    if (user) {
      fetchProfileData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  // Refresh profile data when page becomes visible (e.g., after uploading avatar)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user && !isLoading) {
        fetchProfileData();
      }
    };

    const handleFocus = () => {
      if (user && !isLoading) {
        fetchProfileData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, isLoading]);

  useEffect(() => {
    if (!profileData?.address) return;
    setLocationCoords({
      latitude:
        profileData.address.latitude != null
          ? String(profileData.address.latitude)
          : '',
      longitude:
        profileData.address.longitude != null
          ? String(profileData.address.longitude)
          : '',
      geocoordinates: profileData.address.geocoordinates || '',
    });
  }, [profileData]);

  const fetchProfileData = async () => {
    try {
      setIsLoading(true);

      let profilePayload = null;

      if (isAdmin) {
        let userProfilePayload = null;
        try {
          const userProfileResponse = await get('/user-profile');
          if (userProfileResponse?.success && userProfileResponse.data) {
            userProfilePayload = userProfileResponse.data;
          }
        } catch {
          /* fall through */
        }

        const resolveAccountId = (account) =>
          account?.id || account?._id || account?.uuid || null;

        let accountId =
          user?.accountId ||
          user?.account_id ||
          user?.account?.id ||
          user?.account?._id ||
          userProfilePayload?.accountId ||
          userProfilePayload?.account_id ||
          userProfilePayload?.account?.id ||
          userProfilePayload?.account?._id ||
          null;

        let matchedAccount = null;
        try {
          const accountsResponse = await get('/accounts');
          const accounts = Array.isArray(accountsResponse?.data)
            ? accountsResponse.data
            : Array.isArray(accountsResponse)
              ? accountsResponse
              : [];

          if (accountId) {
            matchedAccount = accounts.find(
              (acc) => resolveAccountId(acc) === accountId
            );
          }

          if (!matchedAccount && user?.email) {
            matchedAccount = accounts.find(
              (acc) =>
                acc.email === user.email ||
                acc.owner_email === user.email ||
                acc.user_email === user.email
            );
            if (matchedAccount) {
              accountId = resolveAccountId(matchedAccount);
            }
          }
        } catch {
          /* fall through */
        }

        if (matchedAccount?.address || matchedAccount?.address_id) {
          profilePayload = matchedAccount;
        } else if (accountId) {
          try {
            const accountResponse = await get(`/accounts/${accountId}`);
            if (accountResponse?.success && accountResponse.data) {
              profilePayload = accountResponse.data;
            } else if (accountResponse && typeof accountResponse === 'object') {
              profilePayload = accountResponse.data || accountResponse;
            }
          } catch {
            if (matchedAccount) {
              profilePayload = matchedAccount;
            }
          }
        }

        if (!profilePayload) {
          profilePayload = matchedAccount || userProfilePayload;
        }
      } else {
        const response = await get('/user-profile');
        if (response?.success && response.data) {
          profilePayload = response.data;
        }
      }


      // For admin users, if accounts API doesn't have avatarUrl or role, try to get it from user-profile
      if (isAdmin && profilePayload) {
        const needsAvatar = !profilePayload.avatarUrl && !profilePayload.avatar && !profilePayload.avatar_url;
        const needsRole = !profilePayload.role;
        
        if (needsAvatar || needsRole) {
          try {
            const userProfileResponse = await get('/user-profile');
            if (userProfileResponse?.success && userProfileResponse.data) {
              if (needsAvatar && userProfileResponse.data.avatarUrl) {
                profilePayload.avatarUrl = userProfileResponse.data.avatarUrl;
              }
              if (needsRole && userProfileResponse.data.role) {
                profilePayload.role = userProfileResponse.data.role;
              }
            }
          } catch (mergeError) {
          }
        }
      }

      if (profilePayload) {
        const mapped = mapProfileResponse(profilePayload, randomAvatar);
        setProfileData(mapped);
        setEditData(mapped);
      } else {
        toast.error('Failed to load profile data.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load profile data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await fetchProfileData();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleEdit = () => {
    if (!profileData) return;
    setIsEditing(true);
    setEditData({ ...profileData });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData(profileData ? { ...profileData } : null);
  };

  const handleSave = async () => {
    try {
      // In a real app, you would update via API
      // const response = await put('/profile', editData);
      // Update the user data in AuthContext if needed
      
      if (editData) {
        setProfileData({ ...editData });
      }
      setIsEditing(false);
      toast.success('Profile updated successfully!');
      
      // Note: In a real implementation, you would also update the user data in AuthContext
      // This would require extending the AuthContext to have an updateUser method
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handleMapLocationChange = ({ latitude, longitude, geocoordinates }) => {
    setLocationCoords({ latitude, longitude, geocoordinates });
  };

  const handleLocationFieldChange = (field, value) => {
    setLocationCoords((prev) => {
      const next = { ...prev, [field]: value };
      const lat = Number.parseFloat(next.latitude);
      const lng = Number.parseFloat(next.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        next.geocoordinates = `${lat.toFixed(6)},${lng.toFixed(6)}`;
      }
      return next;
    });
  };

  const handleSaveLocation = async () => {
    if (!profileData?.id) {
      toast.error('Account not found. Unable to save location.');
      return;
    }

    const latitude = Number.parseFloat(locationCoords.latitude);
    const longitude = Number.parseFloat(locationCoords.longitude);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      toast.error('Please set valid latitude and longitude.');
      return;
    }

    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
      toast.error('Coordinates are out of range.');
      return;
    }

    try {
      setIsSavingLocation(true);
      const geocoordinates =
        locationCoords.geocoordinates || `${latitude.toFixed(6)},${longitude.toFixed(6)}`;

      await put(`/accounts/${profileData.id}`, {
        latitude,
        longitude,
        geocoordinates,
      });

      setProfileData((prev) =>
        prev
          ? {
              ...prev,
              address: {
                ...prev.address,
                latitude,
                longitude,
                geocoordinates,
              },
            }
          : prev
      );
      toast.success('Location coordinates saved.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save location coordinates.');
    } finally {
      setIsSavingLocation(false);
    }
  };

  const renderLocationCoordinatesSection = () => (
    <Card className="border-0 bg-gradient-to-br from-card via-card/95 to-card/90 shadow-xl rounded-3xl overflow-hidden">
      <CardHeader className="border-b border-border/50 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MapPin className="h-5 w-5" />
          Location Coordinates
        </CardTitle>
        <CardDescription>
          Set your account location on the map (Mappls)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="profile-latitude">Latitude</Label>
            <Input
              id="profile-latitude"
              type="number"
              step="any"
              value={locationCoords.latitude}
              onChange={(e) => handleLocationFieldChange('latitude', e.target.value)}
              placeholder="12.9716"
              className="border-1 border-muted-foreground/20"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-longitude">Longitude</Label>
            <Input
              id="profile-longitude"
              type="number"
              step="any"
              value={locationCoords.longitude}
              onChange={(e) => handleLocationFieldChange('longitude', e.target.value)}
              placeholder="77.5946"
              className="border-1 border-muted-foreground/20"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-geocoordinates">Geo Coordinates</Label>
            <Input
              id="profile-geocoordinates"
              value={locationCoords.geocoordinates}
              readOnly
              placeholder="12.9716,77.5946"
              className="border-1 border-muted-foreground/20 bg-muted/30"
            />
          </div>
        </div>

        <LocationMapPicker
          latitude={locationCoords.latitude}
          longitude={locationCoords.longitude}
          onChange={handleMapLocationChange}
        />

        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            className="border-1 border-muted-foreground/20"
            onClick={handleSaveLocation}
            disabled={isSavingLocation}
          >
            {isSavingLocation ? (
              'Saving...'
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Location
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100';
      case 'inactive': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100';
    }
  };

  const getRoleColor = (role) => {
    switch (role.toLowerCase()) {
      case 'super admin': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100';
      case 'admin': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100';
      case 'customer': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100';
    }
  };

  const rating = useMemo(() => {
    if (profileData?.rating != null) {
      return Number(profileData.rating);
    }
    return 0;
  }, [profileData]);
  const renderStars = (value) =>
    Array.from({ length: 5 }).map((_, idx) => (
      <span key={idx} className={idx < Math.round(value) ? 'text-amber-400' : 'text-muted-foreground'}>
        ★
      </span>
    ));

  

  return (
    <div className="flex flex-col min-h-full bg-background">
      {/* Page Header */}
      <div className="flex items-center justify-between gap-4 sm:p-6 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-4 md:hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(settingsPath)}
            className="flex items-center gap-2 text-foreground border-1 border-muted-foreground/20"
          >
            <Settings className="h-4 w-4" />
            <span className="hidden md:inline">Settings</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading || isRefreshing}
            className="flex items-center gap-2 text-foreground border-1 border-muted-foreground/20"
          >
            <RefreshCcw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Mobile spotlight card */}
      {!isLoading && profileData && (
        <div className="md:hidden px-4 py-6">
          <div className="relative mx-auto max-w-sm rounded-[34px] overflow-hidden shadow-[0_45px_90px_rgba(15,23,42,0.85)] mt-4">
            <img
              src={profileData.avatar}
              alt={profileData.name}
              className="h-[520px] w-full object-cover"
            />
            <div className="absolute inset-x-4 bottom-6 rounded-[30px] bg-white/70 dark:bg-black/30 backdrop-blur-2xl border-1 border-muted-foreground/20 p-6 space-y-5 text-slate-900 dark:text-foreground drop-shadow-[0_5px_25px_rgba(0,0,0,0.35)]">
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-600 dark:text-foreground/80">Profile</p>
                  <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">
                    {profileData.name}
                  </h2>
                </div>
                <span className="ml-auto flex items-center justify-center rounded-full bg-slate-900/5 dark:bg-white/10 p-1.5">
                  <Shield className="h-4 w-4 text-slate-900 dark:text-white" />
                </span>
              </div>
              <p className="text-sm text-slate-700 dark:text-white/85">
                {profileData.role} {profileData.location ? `based in ${profileData.location}` : ''}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full border-1 border-muted-foreground/20"
                onClick={() => navigate(settingsPath)}
              >
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Button>
              <div className="flex items-center justify-between text-xs font-medium text-slate-900 dark:text-white">
                <div className="flex items-center gap-2 text-slate-700 dark:text-white">
                  <Users className="h-4 w-4" />
                  <span>{profileData.status || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700 dark:text-white">
                  <Briefcase className="h-4 w-4" />
                  <span>{profileData.role || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700 dark:text-white">
                  <Calendar className="h-4 w-4" />
                  <span>{profileData.joinDate ? new Date(profileData.joinDate).getFullYear() : '—'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="hidden md:flex flex-1 flex-col gap-6 md:gap-8 p-4 md:p-6 lg:p-8 w-full min-w-0 overflow-auto app-content">
        {isLoading ? (
          <Card className="border-0 bg-gradient-to-br from-muted/50 via-muted/30 to-background h-[600px] w-full animate-pulse rounded-2xl shadow-xl" />
        ) : profileData ? (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 w-full">
            {/* Left Sidebar - Profile Card */}
            <Card className="col-span-12 md:col-span-4 border-0 bg-gradient-to-br from-card via-card/95 to-card/90 shadow-2xl rounded-3xl overflow-hidden backdrop-blur-xl h-fit">
              <div className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-8 pb-12">
                <div className="absolute top-0 right-0 w-32 h-32 /5 rounded-full blur-3xl -mr-16 -mt-16"></div>
                <div className="relative z-10 space-y-6">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="relative">
                      <Avatar className="mx-auto h-36 w-36 rounded-3xl border-4 border-background shadow-2xl ring-4 ring-primary/20">
                        <AvatarImage src={profileData.avatar} alt={profileData.name} className="object-cover" />
                        <AvatarFallback className="text-3xl font-bold bg-gradient-to-br from-primary to-primary/60 -foreground">
                          {getInitials(profileData.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="absolute -bottom-2 -right-2 bg-green-500 border-4 border-background rounded-full w-6 h-6 shadow-lg"></div>
                    </div>
                    <div className="text-center space-y-2">
                      <h2 className="text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                        {profileData.name}
                      </h2>
                      <Badge className={`${getRoleColor(profileData.role)} px-4 py-1.5 text-xs font-semibold shadow-md`}>
                        {profileData.role}
                      </Badge>
                     
                    </div>
                  </div>
                </div>
              </div>
              <CardContent className="p-6 space-y-6 bg-background/50">
                <Separator className="bg-border/50" />
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-muted/50 to-muted/30 border-1 border-muted-foreground/20 hover:border-primary/30 transition-all duration-300 group">
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Status</p>
                      <p className={`text-sm font-bold ${profileData.status === 'Active' ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                        {profileData.status || 'N/A'}
                      </p>
                    </div>
                    <Activity className={`h-5 w-5 ${profileData.status === 'Active' ? 'text-green-500' : 'text-yellow-500'} group-hover:scale-110 transition-transform`} />
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-r from-muted/50 to-muted/30 border-1 border-muted-foreground/20">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Member Since</p>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 " />
                      <p className="text-sm font-semibold text-foreground">
                        {profileData.joinDate ? formatDate(profileData.joinDate) : 'Not available'}
                      </p>
                    </div>
                  </div>
                </div>
                <Separator className="bg-border/50" />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-1 border-muted-foreground/20"
                  onClick={() => navigate(settingsPath)}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </Button>
                <Separator className="bg-border/50" />
                
              </CardContent>
            </Card>

            {/* Right Content - Details Card */}
            <Card className="col-span-12 md:col-span-8 border-0 bg-gradient-to-br from-card via-card/95 to-card/90 shadow-2xl rounded-3xl overflow-hidden backdrop-blur-xl h-fit">
              <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 border-b border-border/50">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h2 className="text-3xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                        {profileData.name}
                      </h2>
                      <Badge className={`${getRoleColor(profileData.role)} px-3 py-1 text-xs font-semibold shadow-md`}>
                        {profileData.role}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span className="text-sm">{profileData.location || 'Location not set'}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-3">
                     
                      <div className="h-6 w-px bg-border"></div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span className="font-medium">{profileData.status || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" size="sm" onClick={() => toast.info('Messaging coming soon')} className="shadow-sm hover:shadow-md transition-all duration-200 border-primary/30 hover:border-primary/60">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Message
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toast.info('Contacts action coming soon')} className="shadow-md hover:shadow-lg transition-all duration-200">
                      <PhoneCall className="h-4 w-4 mr-2" />
                      Contact
                    </Button>
                  </div>
                </div>
              </div>
              <CardContent className="p-6 lg:p-8">
               
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="h-1 w-1 rounded-full"></div>
                        <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Contact Information</p>
                      </div>
                      <div className="space-y-4">
                        <div className="group p-4 rounded-xl bg-gradient-to-br from-muted/30 to-muted/10 border-1 border-muted-foreground/20 hover:border-primary/30 hover:shadow-md transition-all duration-300">
                          <div className="flex items-center gap-3 mb-2">
                            <Phone className="h-4 w-4 " />
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Phone</p>
                          </div>
                          <a href={`tel:${profileData.phone}`} className="text-sm font-semibold text-foreground hover: transition-colors">
                            {profileData.phone || 'Not provided'}
                          </a>
                        </div>
                        <div className="group p-4 rounded-xl bg-gradient-to-br from-muted/30 to-muted/10 border-1 border-muted-foreground/20 hover:border-primary/30 hover:shadow-md transition-all duration-300">
                          <div className="flex items-center gap-3 mb-2">
                            <MapPin className="h-4 w-4 " />
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Full Address</p>
                          </div>
                          <p className="text-sm font-semibold text-foreground">{profileData.location || profileData.address?.fullAddress || 'Not provided'}</p>
                        </div>
                        <div className="group p-4 rounded-xl bg-gradient-to-br from-muted/30 to-muted/10 border-1 border-muted-foreground/20 hover:border-primary/30 hover:shadow-md transition-all duration-300">
                          <div className="flex items-center gap-3 mb-2">
                            <Mail className="h-4 w-4 " />
                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">E-mail</p>
                          </div>
                          <a href={`mailto:${profileData.email}`} className="text-sm font-semibold text-foreground hover: transition-colors break-all">
                            {profileData.email || 'Not provided'}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div>
                     
                      
                    </div>
                    {profileData.address && (
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <div className="h-1 w-1 rounded-full "></div>
                          <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Address Details</p>
                        </div>
                        <div className="space-y-4">
                          {/* Primary Address Card */}
                          <div className="group p-4 rounded-xl bg-gradient-to-br from-muted/30 to-muted/10 border-1 border-muted-foreground/20 hover:border-primary/30 hover:shadow-md transition-all duration-300">
                            <div className="flex items-center gap-3 mb-3">
                              <MapPin className="h-4 w-4 " />
                              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Street Address</p>
                            </div>
                            <div className="space-y-2 text-sm">
                              {(profileData.address.houseNo || profileData.address.street) && (
                                <p className="font-semibold text-foreground">
                                  {[profileData.address.houseNo, profileData.address.street].filter(Boolean).join(', ')}
                                </p>
                              )}
                              {(profileData.address.subArea || profileData.address.area) && (
                                <p className="text-muted-foreground">
                                  {[profileData.address.subArea, profileData.address.area].filter(Boolean).join(', ')}
                                </p>
                              )}
                              {profileData.address.landmark && (
                                <p className="text-xs text-muted-foreground">
                                  <span className="font-medium">Landmark:</span> {profileData.address.landmark}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Location & Pincode Card */}
                          {(profileData.address.pincode || profileData.address.latitude) && (
                            <div className="group p-4 rounded-xl bg-gradient-to-br from-muted/30 to-muted/10 border-1 border-muted-foreground/20 hover:border-primary/30 hover:shadow-md transition-all duration-300">
                              <div className="flex items-center gap-3 mb-3">
                                <MapPin className="h-4 w-4 " />
                                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Location Details</p>
                              </div>
                              <div className="space-y-2 text-sm">
                                {profileData.address.pincode && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground font-medium">Pincode:</span>
                                    <span className="font-semibold text-foreground">{profileData.address.pincode}</span>
                                  </div>
                                )}
                                {(profileData.address.latitude && profileData.address.longitude) && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground font-medium">Coordinates:</span>
                                    <span className="font-semibold text-foreground">
                                      {profileData.address.latitude}, {profileData.address.longitude}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="border-0 bg-gradient-to-br from-muted/30 via-muted/20 to-background h-[600px] w-full flex items-center justify-center rounded-2xl shadow-xl">
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center">
                <User className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground font-medium">No profile data available.</p>
            </div>
          </Card>
        )}

      </div>

      {isAdmin && profileData && (
        <div className="w-full px-4 pb-6 md:px-6 lg:px-8 app-content">
          {renderLocationCoordinatesSection()}
        </div>
      )}
    </div>
  );
}
